import { useEffect, useMemo, useState } from "react";
import { useSite } from "@/context/SiteContext";
import { Link } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { SectionHead } from "@/components/common";
import { ResourceGrid } from "@/components/ResourceCard";
import { GrowthListBlock } from "@/components/EmailCapture";
import { LeadMagnet, CommunityPromo } from "@/components/Promos";
import { ArrowUpRight, Users } from "lucide-react";

export default function FreeHub() {
  const { settings } = useSite();
  const fh = settings.free_hub;
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("");
  useEffect(() => {
    api.get("/resources", { params: { access: "free" } }).then((r) => setItems(r.data));
  }, []);
  const featured = items.filter((r) => r.featured);
  const challenges = useMemo(() => settings.challenges.filter((c) => c.published !== false), [settings.challenges]);
  const rest = items.filter((r) => !r.featured);
  const cats = [...new Set(rest.map((r) => r.category).filter(Boolean))];
  const shown = cat ? rest.filter((r) => r.category === cat) : rest;

  return (
    <div className="container-bt py-8 sm:py-12" data-testid="free-hub-page">
      {settings.free_hub_banner?.enabled && (
        <a href={settings.community.url} target="_blank" rel="noopener noreferrer" data-testid="free-hub-community-banner"
          className="mb-12 flex flex-col gap-4 rounded-[24px] bg-bt-primary p-5 text-bt-bg transition-transform duration-300 hover:-translate-y-0.5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <span className="flex items-center gap-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/15"><Users size={20} /></span>
            <span className="text-base font-medium sm:text-lg">{settings.free_hub_banner.text}</span>
          </span>
          <span className="inline-flex min-h-[48px] shrink-0 items-center justify-center gap-2 rounded-full bg-bt-bg px-6 font-semibold text-bt-primary">{settings.free_hub_banner.cta} <ArrowUpRight size={18} /></span>
        </a>
      )}
      <div className="fade-up mb-14 max-w-3xl">
        <p className="eyebrow mb-4">Free, always</p>
        <h1 className="text-4xl leading-tight sm:text-5xl lg:text-6xl">{fh.headline}</h1>
        <p className="mt-5 text-base md:text-lg">{fh.intro}</p>
      </div>
      {featured.length > 0 && (
        <section className="mb-20">
          <SectionHead title={fh.featured_heading} />
          <ResourceGrid items={featured} testid="free-hub-featured" />
        </section>
      )}
      <section className="mb-20">
        <SectionHead title={fh.more_heading} />
        {cats.length > 1 && (
          <div className="-mx-5 mb-8 flex gap-2 overflow-x-auto px-5 pb-2" data-testid="free-hub-filters">
            <button className={`chip shrink-0 ${!cat ? "chip-active" : ""}`} onClick={() => setCat("")} data-testid="free-filter-all">All</button>
            {cats.map((c) => (
              <button key={c} className={`chip shrink-0 ${cat === c ? "chip-active" : ""}`} onClick={() => setCat(c)} data-testid={`free-filter-${c.toLowerCase().replace(/\W+/g, "-")}`}>{c}</button>
            ))}
          </div>
        )}
        <ResourceGrid items={shown} testid="free-hub-grid" />
      </section>
      {settings.challenges?.filter((c) => c.published !== false).length > 0 && (
        <section className="mb-20" data-testid="free-hub-challenges">
          <SectionHead eyebrow="Free with an account" title="Growth challenges" />
          <div className="grid gap-5 md:grid-cols-2">
            {challenges.map((c) => (
              <Link key={c.slug} to={`/challenge/${c.slug}`} data-testid={`challenge-card-${c.slug}`}
                className="group card-bt flex items-center gap-5 p-4 transition-[transform,border-color] duration-300 hover:-translate-y-0.5 hover:border-bt-primary">
                {c.image && <img src={mediaUrl(c.image)} alt="" className="h-24 w-24 shrink-0 rounded-[22px] object-cover" />}
                <div>
                  <p className="eyebrow mb-1">{c.days?.length} days</p>
                  <h3 className="text-xl">{c.title}</h3>
                  <p className="mt-1 text-sm">{c.subtitle}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
      {settings.bedtime?.enabled && (
        <Link to="/bedtime" className="mb-20 flex items-center justify-between gap-5 rounded-[28px] bg-bt-primary p-7 text-bt-bg transition-transform duration-300 hover:-translate-y-0.5" data-testid="free-hub-bedtime-link">
          <span><span className="block font-heading text-2xl" style={{ color: "var(--bt-background)" }}>{settings.bedtime.title}</span><span className="text-sm opacity-85">Calm affirmations, played one after another</span></span>
          <span className="shrink-0 rounded-full bg-bt-bg px-5 py-3 text-sm font-semibold text-bt-primary">Listen</span>
        </Link>
      )}
      <div className="space-y-12">
        <GrowthListBlock source="free_hub" />
        <LeadMagnet />
        {settings.community.show_after_content && <CommunityPromo compact />}
      </div>
    </div>
  );
}
